import {stringify} from 'query-string';
import {CREATE, fetchUtils, GET_LIST, GET_MANY, GET_MANY_REFERENCE, GET_ONE,} from 'react-admin';
import {d4UrlSchema} from "./Env";

// Helper function to process JSON data
const processJson = (json) => {
    // console.debug("processJson", json)
    // Process date fields
    Object.keys(json).forEach(key => {
        if (json[key]['@type'] === 'http://www.w3.org/2001/XMLSchema#dateTime') {
            const date = new Date(json[key]['@value']);
            json[key] = !isNaN(date.getTime()) ? date : json[key];
        }
    });

    // Process specific fields
    [
        'isPartOf',
        'containedIn',
        'responsibleOrganisation'
    ].forEach(term => {
        const key = Object.keys(json).find(k => k.includes(term));
        if (key && typeof json[key] === 'object' && '@id' in json[key]) {
            json[key] = json[key]['@id'];
        }
    });

    // Process array fields
    [
        'hasPart',
        'roleDeliversTo',
        'providesFunction',
        'usesFunction',
        'containsProduct',
        'roleClientOf',
        'implementedBy',
        'usedBy',
        'functionalResponsibility',
        'productResponsibility',
        'consumer'
    ].forEach(term => {
        const key = Object.keys(json).find(k => k.includes(term));
        if (key) {
            json[key] = Array.isArray(json[key]) ? json[key] : [json[key]];
            json[key] = json[key].map(x => x['@id'] || x);
        }
    });

    // Process properties
    const propertiesKey = Object.keys(json).find(k => k.includes('properties'));
    if (propertiesKey) {
        json[propertiesKey] = Array.isArray(json[propertiesKey]) ? json[propertiesKey] : [json[propertiesKey]];
    }

    // Rename keys with d4UrlSchema
    Object.keys(json).forEach(key => {
        if (key.includes(d4UrlSchema)) {
            const newKey = key.replace(d4UrlSchema, "").replace(/\//g, "_");
            json[newKey] = json[key];
            delete json[key];
        }
    });

    // console.debug("processJson return:", json)
    return json;
};

// Main data provider function
export default (apiUrl, httpClient = fetchUtils.fetchJson) => {
    const convertDataRequestToHTTP = (type, resource, params) => {
        let url = '';
        const options = {};

        switch (type) {
            case GET_LIST: {
                // console.debug("convertDataRequestToHTTP / GET_LIST", params, " =>")
                const {page, perPage} = params.pagination;
                const {field, order} = params.sort;
                const query = {
                    ...fetchUtils.flattenObject(params.filter),
                    sort: field,
                    order: order,
                    start: (page - 1) * perPage,
                    end: page * perPage
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                break;
            }
            case GET_ONE:
                // console.debug("convertDataRequestToHTTP / GET_ONE", params, " =>")
                url = `${params.id}?${stringify({})}`;
                break;
            case GET_MANY_REFERENCE: {
                // console.debug("convertDataRequestToHTTP / GET_MANY_REFERENCE", params, " =>")
                const {page, perPage} = params.pagination;
                const {field, order} = params.sort;
                const query = {
                    ...fetchUtils.flattenObject(params.filter),
                    sort: field,
                    order: order,
                    start: (page - 1) * perPage,
                    end: page * perPage,
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                options.method = 'POST';
                options.body = JSON.stringify({'ids': params.id});
                break;
            }
            case GET_MANY: {
                // console.debug("convertDataRequestToHTTP / GET_MANY", params, " =>")
                const query = {
                    order: "ASC",
                    sort: "name",
                    compact: `${d4UrlSchema}/base.jsonld`
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                options.method = 'POST';
                options.body = JSON.stringify({'ids': params.ids});
                break;
            }
            default:
                throw new Error(`Unsupported fetch action type ${type}`);
        }

        return {url, options};
    };

    const convertHTTPResponse = (response, type, resource, params) => {
        const {headers, json} = response;

        if (Array.isArray(json)) {
            json.forEach(elem => elem.id = elem['@id']);
        } else {
            json.id = json['@id'];
        }

        switch (type) {
            case GET_LIST:
            case GET_MANY_REFERENCE:
                // console.debug("convertHTTPResponse / GET_LIST", params, " =>")
                if (!headers.has('x-total-count')) {
                    throw new Error('The X-Total-Count header is missing in the HTTP Response.');
                }
                return {
                    data: Array.isArray(json) ? json.map(processJson) : [processJson(json)],
                    total: parseInt(headers.get('x-total-count').split('/').pop(), 10),
                };
            case GET_MANY:
                // console.debug("convertHTTPResponse / GET_MANY", params, " =>")
                if (!headers.has('x-total-count')) {
                    throw new Error('The X-Total-Count header is missing in the HTTP Response.');
                }
                return {
                    data: Array.isArray(json) ? json : [json],
                };
            case GET_ONE:
                // console.debug("convertHTTPResponse / GET_ONE", params, " =>")
                return {data: processJson(json)};
            case CREATE:
                return {data: {...params.data, id: json.id}};
            default:
                return {data: json};
        }
    };

    return (type, resource, params) => {
        const {url, options} = convertDataRequestToHTTP(type, resource, params);
        return httpClient(url, options).then(response =>
            convertHTTPResponse(response, type, resource, params)
        );
    };
};